/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import ROUTES_ENUMS from "routes/url.enum";
import { getDefaultDayNightPackage } from "utils/CommonFun";
import { usePackageSearch } from "api/queries/usePackageSearch";

function SearchBar({ onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const { data: searchResults, isLoading } = usePackageSearch(searchTerm);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const debouncedSetSearch = debounce((value) => {
    setSearchTerm(value);
    setShowDropdown(true);
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setSearchTerm("");
      setShowDropdown(false);
      return;
    }
    debouncedSetSearch(value);
  };

  const handleResultClick = (packageId) => {
    onClose();
    setShowDropdown(false);
    setSearchTerm("");
    setTimeout(() => {
      navigate(`${ROUTES_ENUMS.TOUR_DETAILS.replace(':packageId', packageId)}`);
    }, 1000);
  };

  return (
    <div className="relative" ref={searchRef}>
      <form 
        className="flex flex-wrap gap-2.5 items-center self-center px-5 py-2.5 mt-4 max-w-full text-sm whitespace-nowrap border border-white border-solid rounded-[50px] text-white text-opacity-60 w-[507px]"
        onSubmit={(e) => e.preventDefault()}
      >
        <label htmlFor="searchInput" className="sr-only">
          Search
        </label>
        <img
          loading="lazy"
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/a639628e2e0559ac2fcbf66c7414b886a66814c75a8d8661827586a0ebf156cc"
          alt=""
          className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
        />
        <input
          type="search"
          id="searchInput"
          onChange={handleSearchChange}
          placeholder="Search destinations..."
          className="bg-transparent border-none outline-none text-white text-opacity-60 flex-grow"
        />
        {isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
        )}
      </form>

      {/* Dropdown Results */}
      {showDropdown && searchResults && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-[#1C1C1C] border border-gray-700 rounded-lg shadow-lg max-h-[400px] overflow-y-auto">
          {searchResults.map((result) => (
            <div
              key={result._id}
              onClick={() => handleResultClick(result._id)}
              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-800 border-b border-gray-700 last:border-none"
            >
              <img 
                src={result.imageUrl} 
                alt={result.name}
                className="w-12 h-12 object-cover rounded"
              />
              <div className="flex flex-col">
                <span className="text-white text-sm font-medium">{result.name}</span>
                <span className="text-gray-400 text-xs">
                  {result.shortDescription.substring(0, 60)}...
                </span>
                <div className="flex items-center gap-2 mt-1">
                  {(() => {
                    const dp = getDefaultDayNightPackage(result);
                    const price = dp?.price ?? 0;
                    const duration = dp?.duration ?? '';
                    return (
                      <>
                        <span className="text-orange-500 text-xs font-medium">
                          From ₹{Number(price).toLocaleString('en-IN')}
                        </span>
                        <span className="text-gray-400 text-xs">{duration}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
